import {
  InputAdornment,
  InputProps as MaterialBaseInputProps,
  TextField as MaterialTextField,
  TextFieldProps as MaterialTextFieldProps,
} from '@material-ui/core';
import { isNil } from 'lodash';
import React from 'react';
import { width, WidthProps } from 'styled-system';
import { styled } from '~ui-kit/styled-components';
import { Box } from '../box';
import { InputLabel, InputLabelProps } from '../input-label';
import { InputMask } from './mask/input-mask';
import { InputMaskConfig } from './mask/mask-helpers';

export interface TextInputProps extends Omit<MaterialTextFieldProps, 'variant'>, WidthProps {
  variant?: 'standard' | 'outlined';
  maxLength?: number;
  inputMaskConfig?: InputMaskConfig;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  required?: boolean;
  inputLabelProps?: InputLabelProps;
}

export function TextInput({
  variant = 'standard',
  fullWidth = true,
  type,
  maxLength,
  inputMaskConfig,
  label,
  InputProps,
  startIcon,
  endIcon,
  id,
  required,
  inputLabelProps,
  ...props
}: TextInputProps) {
  const extraProps: Record<string, any> = {};

  if (type === 'password') {
    extraProps['autoComplete'] = 'current-password';
  }

  const muiInputProps: Partial<MaterialBaseInputProps> = {
    // Props for the MUI's Input element
    ...InputProps,

    // Props for the base "input" html element
    inputProps: { ...InputProps?.inputProps },
  };

  if (!isNil(startIcon)) {
    muiInputProps.startAdornment = <InputAdornment position="start">{startIcon}</InputAdornment>;
  }

  if (!isNil(endIcon)) {
    muiInputProps.endAdornment = <InputAdornment position="end">{endIcon}</InputAdornment>;
  }

  if (!isNil(maxLength)) {
    muiInputProps.inputProps.maxLength = maxLength;
  }

  // Props needed for the mask to give to the base input element used by Material UI
  if (!isNil(inputMaskConfig)) {
    muiInputProps.inputComponent = InputMask;
    muiInputProps.inputProps.mask = inputMaskConfig.mask;
    muiInputProps.inputProps.pipe = inputMaskConfig.maskPostOperation;
  }

  const InputElement = (
    <StyledTextInput
      // Make sure Clarity masks the input's content (should be masking by default either way)
      data-clarity-mask="true"
      type={type}
      variant={variant}
      id={id}
      fullWidth={props.width ? false : fullWidth}
      InputProps={muiInputProps}
      {...props}
      {...extraProps}
    />
  );

  if (label) {
    return (
      <Box flexDirection="column">
        <InputLabel htmlFor={id} required={required} $mb={2} {...inputLabelProps}>
          {label}
        </InputLabel>
        {InputElement}
      </Box>
    );
  }

  return InputElement;
}

const StyledTextInput = styled(MaterialTextField)<MaterialTextFieldProps & WidthProps>`
  /* Align input placeholder in the middle - Fix for safari 12 */
  line-height: normal;
  ${width}

  .MuiInputBase-root {
    background-color: ${({ theme }) => theme.colors.white};
  }
`;
